/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, takeEvery, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../../../modules/notifications/actions';
import { FORM_NAME, FORM_FIELDS, REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../../../api/constants';
import { errorHandler } from '../../../../api/utils';
import { GROUP_TO_GROUP, WAY_TO_GROUP, ORDER_GROUP_TO_GROUP, ORDER_WAY_TO_GROUP } from './actions';
import {
	groupToGroupRequest,
	orderGroupToGroupRequest,
	orderWayToGroupRequest,
	wayToGroupRequest,
} from '../../../../api/requests';
import { getFormIsInvalidGroupSelector } from './selectors';
import {
	getGroupsListAction,
	getCollectionGroupListAction,
	getCollectionWayListAction,
	getWaysGroupListAction,
	setIsLoadingAction,
	getOrdersCollectionGroupListAction,
	getOrdersCollectionWayListAction,
	getGroupsWithWaysListAction,
} from '../../actions';
import {
	getCollectionGroupOpenSelector,
	getCollectionWayOpenSelector,
	getGroupOpenSelector,
	getGroupWayOpenSelector,
	getWayOpenSelector,
} from '../../selectors';
import { transformToValidationErrors } from './utils';

export function* groupToGroupSaga({ payload: { searchQuery, group, to_id, callback } }) {
	try {
		const groupOpen = yield select(getGroupOpenSelector());

		const isInvalid = yield select(getFormIsInvalidGroupSelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = yield call(groupToGroupRequest, group.id, group.process?.id, {
			group_id: to_id,
		});

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));

			if (groupOpen && (groupOpen.id === group.id || groupOpen.id === to_id)) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	}
}

export function* wayToGroupSaga({ payload: { searchQuery, way, group_id, callback } }) {
	try {
		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const { data, message, toast, errors } = yield call(wayToGroupRequest, way.id, { group_id });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen && groupOpen.id === group_id) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery, noLoading: true }));
			}

			if (wayOpen && wayOpen.id === way.id && !wayOpen?.planning) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery, noLoading: true }));
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	}
}

export function* orderGroupToGroupSaga({ payload: { searchQuery, collection, orders, group, to_id, callback } }) {
	try {
		yield put(setIsLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

		const isInvalid = yield select(getFormIsInvalidGroupSelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = collection
			? yield call(orderGroupToGroupRequest, {
					collection,
					from_id: group.id,
					to_id,
			  })
			: yield call(orderGroupToGroupRequest, {
					orders,
					from_id: group.id,
					to_id,
			  });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));

			if (groupOpen && (groupOpen.id === group.id || groupOpen.id === to_id)) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}

			if (groupOpen && collectionGroupOpen) {
				yield put(
					getOrdersCollectionGroupListAction({
						row: groupOpen,
						collection: collectionGroupOpen,
						searchQuery,
					}),
				);
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLoadingAction(false));
	}
}

export function* orderWayToGroupSaga({ payload: { searchQuery, collection, orders, way, group_id, callback } }) {
	try {
		yield put(setIsLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

		const collectionWayOpen = yield select(getCollectionWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidGroupSelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = collection
			? yield call(orderWayToGroupRequest, { collection, way_id: way.id, group_id })
			: yield call(orderWayToGroupRequest, { orders, way_id: way.id, group_id });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen && groupOpen.id === group_id) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));

				if (collectionGroupOpen) {
					yield put(
						getOrdersCollectionGroupListAction({
							row: groupOpen,
							collection: collectionGroupOpen,
							searchQuery,
						}),
					);
				}
			}

			if (wayOpen && wayOpen.id === way.id) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery }));

				if (collectionWayOpen) {
					yield put(
						getOrdersCollectionWayListAction({
							row: wayOpen,
							collection: collectionWayOpen,
							searchQuery,
						}),
					);
				}
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLoadingAction(false));
	}
}

export default function* orderGroupsChangeGroupContainerSaga() {
	yield takeEvery(GROUP_TO_GROUP, groupToGroupSaga);
	yield takeEvery(WAY_TO_GROUP, wayToGroupSaga);
	yield takeEvery(ORDER_GROUP_TO_GROUP, orderGroupToGroupSaga);
	yield takeEvery(ORDER_WAY_TO_GROUP, orderWayToGroupSaga);
}
